import React, { useEffect, useRef, useState } from 'react';
import OnboardingShell from '../components/OnboardingShell';
import { useMedia } from '../context/MediaContext';
import { isSecureMediaContext, insecureMediaMessage, canScreenShare, screenShareUnsupportedMessage } from '../../../utils/mediaSupport';

/**
 * Step 1 — Permission verification. Camera (live preview), microphone (live level meter + speech
 * detection), and entire-screen sharing (validated). All required checks must pass before Continue.
 * Streams are owned by MediaProvider so they persist into the rest of the flow.
 */
const StatusPill = ({ ok }) => (
  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${ok
    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300'
    : 'bg-slate-200 text-slate-500 dark:bg-white/10 dark:text-slate-400'}`}>
    {ok ? 'Verified' : 'Pending'}
  </span>
);

const Card = ({ title, ok, children }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-900/40">
    <div className="mb-3 flex items-center justify-between">
      <span className="text-sm font-semibold">{title}</span>
      <StatusPill ok={ok} />
    </div>
    {children}
  </div>
);

const PermissionStep = ({ steps, completed, config, onContinue }) => {
  const { camera, screen, capture, startMic } = useMedia();
  const req = (config && config.interview) || {};
  const cameraRequired = req.camera_required !== false;
  const micRequired = req.mic_required !== false;
  const screenRequired = req.screen_required !== false;

  const videoRef = useRef(null);
  const [micHeard, setMicHeard] = useState(false);
  const [shareWarn, setShareWarn] = useState('');
  const [busy, setBusy] = useState('');

  useEffect(() => { if (videoRef.current && camera.stream) videoRef.current.srcObject = camera.stream; }, [camera.stream]);
  useEffect(() => { if (capture.level > 0.04) setMicHeard(true); }, [capture.level]);

  const enableCamera = async () => { setBusy('cam'); try { await camera.start(); } catch { /* error in camera.error */ } setBusy(''); };
  const enableMic = async () => { setBusy('mic'); try { await startMic(); } catch { /* ignore */ } setBusy(''); };
  const shareScreen = async () => {
    setBusy('screen'); setShareWarn('');
    const r = await screen.start();
    if (!r.ok) {
      setShareWarn(r.reason === 'not_entire_screen'
        ? 'Please share your ENTIRE screen (the whole monitor), not a window or browser tab.'
        : r.reason === 'denied' ? 'Screen sharing was blocked. Allow it to continue.'
          : r.reason === 'unsupported' ? (r.error || screenShareUnsupportedMessage())
            : r.reason === 'insecure' ? (r.error || insecureMediaMessage())
              : 'Could not start screen sharing. Please try again.');
    }
    setBusy('');
  };

  const camOk = !cameraRequired || !!camera.stream;
  const micOk = !micRequired || capture.active || micHeard;   // mic running is enough; speech is bonus feedback
  const screenOk = !screenRequired || screen.sharing;
  const canContinue = camOk && micOk && screenOk;

  const level = Math.min(1, capture.level * 1.6);

  // The UI is driven by the interview config: only the required devices are shown/requested. This is
  // why a mock interview (mic only) never renders camera/screen cards, and an HR interview with screen
  // sharing disabled stops asking for it — nothing is hidden-but-still-requested.
  const needed = [
    cameraRequired && 'camera',
    micRequired && 'microphone',
    screenRequired && 'your entire screen',
  ].filter(Boolean);
  const neededText = needed.length > 1
    ? `${needed.slice(0, -1).join(', ')} and ${needed[needed.length - 1]}`
    : (needed[0] || '');
  const subtitle = needed.length
    ? `We need access to your ${neededText} to run a fair interview.`
    : 'A quick device check before your interview.';

  return (
    <OnboardingShell
      steps={steps} completed={completed} activeKey="permission" recording={screen.sharing}
      title="Permission verification"
      subtitle={subtitle}
    >
      {!isSecureMediaContext() && (
        <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-200">
          {insecureMediaMessage()}
        </div>
      )}

      {/* Secure context but screen capture unavailable (phone/tablet) — guide to a laptop up front,
          rather than letting them pass camera/mic and then hit a confusing wall on the screen step. */}
      {isSecureMediaContext() && screenRequired && !canScreenShare() && (
        <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-200">
          {screenShareUnsupportedMessage()}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        {/* camera */}
        {cameraRequired && <Card title="Camera access" ok={camOk}>
          <div className="relative aspect-video overflow-hidden rounded-xl bg-slate-100 dark:bg-slate-800">
            <video ref={videoRef} autoPlay playsInline muted
              className={`h-full w-full object-cover [transform:scaleX(-1)] ${camera.stream ? '' : 'hidden'}`} />
            {!camera.stream && (
              <div className="flex h-full items-center justify-center">
                <button type="button" onClick={enableCamera} disabled={busy === 'cam'}
                  className="rounded-full bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 dark:bg-violet-600 dark:hover:bg-violet-500">
                  {busy === 'cam' ? 'Enabling…' : 'Enable camera'}
                </button>
              </div>
            )}
          </div>
          {camera.error && <p className="mt-2 text-xs text-rose-500">{camera.error}</p>}
        </Card>}

        {/* microphone */}
        {micRequired && <Card title="Microphone access" ok={micOk}>
          <div className="flex h-full flex-col justify-center gap-3 py-2">
            <div className="h-3 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
              <div className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-primary-500 transition-[width] duration-75"
                style={{ width: `${Math.round(level * 100)}%` }} />
            </div>
            {!capture.active ? (
              <button type="button" onClick={enableMic} disabled={busy === 'mic'}
                className="self-start rounded-full bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 dark:bg-violet-600 dark:hover:bg-violet-500">
                {busy === 'mic' ? 'Enabling…' : 'Enable microphone'}
              </button>
            ) : (
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {micHeard ? 'Microphone detected — sounds good.' : 'Say a few words to test your microphone…'}
              </p>
            )}
            {capture.error && <p className="text-xs text-rose-500">{capture.error}</p>}
          </div>
        </Card>}

        {/* screen share */}
        {screenRequired && <Card title="Entire screen sharing" ok={screenOk}>
          <div className="flex h-full flex-col justify-center gap-3 py-2">
            {screen.sharing ? (
              <p className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-300">
                <span className="h-2 w-2 rounded-full bg-emerald-500" /> Entire screen is being shared
              </p>
            ) : (
              <button type="button" onClick={shareScreen} disabled={busy === 'screen'}
                className="self-start rounded-full bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 dark:bg-violet-600 dark:hover:bg-violet-500">
                {busy === 'screen' ? 'Requesting…' : 'Share entire screen'}
              </button>
            )}
            {shareWarn && <p className="text-xs text-amber-600 dark:text-amber-300">{shareWarn}</p>}
            <p className="text-[11px] text-slate-400">Sharing begins a proctored screen recording for this session.</p>
          </div>
        </Card>}

        {/* continue */}
        <div className="flex items-end sm:col-span-1">
          <button type="button" onClick={onContinue} disabled={!canContinue}
            className="w-full rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 px-4 py-3 text-sm font-semibold text-white shadow-lg transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-40">
            {canContinue ? 'Continue' : 'Complete all checks to continue'}
          </button>
        </div>
      </div>
    </OnboardingShell>
  );
};

export default PermissionStep;
